/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components

import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonAvatar from "components/ArgonAvatar";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import breakpoints from "assets/theme/base/breakpoints";

// Data
// import authorsTableData from "layouts/tables/data/authorsTableData";
import projectsTableData from "layouts/tables/data/projectsTableData";
// import tutorTableData from "Views/people/tutor/data/tutorTableData";
// import TestUserTableData from "Views/TestUser/data/TestUserTableData"

import { useState, useEffect } from "react";
import { getIncomeBreakdown } from "services/financeService";

import dayjs from "dayjs";
import CustomDatePicker from "components/CustomDatePicker/CustomDatePicker";
import ArgonInput from "components/ArgonInput";
import ApnaTabs from "components/ApnaTabs/ApnaTabs";
import PaginationBar from "components/PaginationBar/PaginationBar";
import ArgonButton from "components/ArgonButton";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import VisibilityIcon from "@mui/icons-material/Visibility";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function Tables() {
  // const { columns, rows } = IncomeBreakDownTabelData;
  const [dob, setDob] = useState(dayjs());
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [activeTab, setActiveTab] = useState("Clients");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [tableRows, setTableRows] = useState([]);
  const [isStudentsModalOpen, setIsStudentsModalOpen] = useState(false);
  const [selectedClassName, setSelectedClassName] = useState("");
  const [selectedClassStudents, setSelectedClassStudents] = useState([]);

  const isCategoryStandardTab = activeTab === "Category_Standard";

  const formatCurrency = (amount) => `₹${Number(amount || 0).toLocaleString("en-IN")}`;

  const getDisplayNetSalary = (item) => {
    if (typeof item?.netSalary === "number" && item.netSalary > 0) {
      return item.netSalary;
    }

    if (Array.isArray(item?.cycleDetails)) {
      const cycleTutorSalary = item.cycleDetails.reduce((sum, cycle) => {
        return sum + (Number(cycle?.tutorSalary) || 0);
      }, 0);

      if (cycleTutorSalary > 0) {
        return cycleTutorSalary;
      }
    }

    return Number(item?.netSalary) || 0;
  };

  const getComputedMargin = (item, resolvedNetSalary) => {
    const tuitionFee = Number(item?.tuitionFee) || 0;
    const netSalary =
      typeof resolvedNetSalary === "number" ? resolvedNetSalary : getDisplayNetSalary(item);
    return tuitionFee - netSalary;
  };

  const buildClassWiseBreakdown = (apiRows = []) => {
    const classMap = new Map();

    apiRows.forEach((row) => {
      const cycleDetails = Array.isArray(row?.cycleDetails) ? row.cycleDetails : [];

      cycleDetails.forEach((cycle) => {
        const rawClassNames = Array.isArray(cycle?.classNames) ? cycle.classNames : [];
        const classNames = [...new Set(rawClassNames.filter(Boolean))];
        const resolvedClassNames = classNames.length > 0 ? classNames : ["Unassigned"];
        const rawStudents = Array.isArray(cycle?.students)
          ? cycle.students
          : cycle?.students
          ? [cycle.students]
          : [];
        const students = rawStudents.filter(Boolean);

        const tuitionFee = Number(cycle?.tuitionFee) || 0;
        const tutorSalary = Number(cycle?.tutorSalary) || 0;
        const splitFactor = resolvedClassNames.length;
        const tuitionShare = tuitionFee / splitFactor;
        const netSalaryShare = tutorSalary / splitFactor;
        const marginShare = tuitionShare - netSalaryShare;

        resolvedClassNames.forEach((className) => {
          if (!classMap.has(className)) {
            classMap.set(className, {
              className,
              tuitionFee: 0,
              netSalary: 0,
              margin: 0,
              students: new Set(),
            });
          }

          const classEntry = classMap.get(className);
          classEntry.tuitionFee += tuitionShare;
          classEntry.netSalary += netSalaryShare;
          classEntry.margin += marginShare;
          students.forEach((studentName) => classEntry.students.add(studentName));
        });
      });
    });

    return Array.from(classMap.values())
      .map((item) => {
        const roundedTuitionFee = Math.round(item.tuitionFee || 0);
        const roundedNetSalary = Math.round(item.netSalary || 0);
        const roundedMargin = Math.round((item.tuitionFee || 0) - (item.netSalary || 0));
        return {
          className: item.className,
          tuitionFee: roundedTuitionFee,
          netSalary: roundedNetSalary,
          margin: roundedMargin,
          students: Array.from(item.students || []).sort((a, b) => a.localeCompare(b)),
          marginPercentage:
            roundedTuitionFee > 0 ? Math.round((roundedMargin / roundedTuitionFee) * 100) : 0,
        };
      })
      .sort((a, b) => b.tuitionFee - a.tuitionFee);
  };

  const openStudentsModal = (className, students = []) => {
    setSelectedClassName(className || "Unassigned");
    setSelectedClassStudents(Array.isArray(students) ? students : []);
    setIsStudentsModalOpen(true);
  };

  const totals = data.reduce(
    (acc, item) => {
      const tuitionFee = Number(item?.tuitionFee) || 0;
      const netSalary = getDisplayNetSalary(item);
      acc.tuitionFee += tuitionFee;
      acc.netSalary += netSalary;
      acc.margin += tuitionFee - netSalary;
      return acc;
    },
    { tuitionFee: 0, netSalary: 0, margin: 0 }
  );

  const tuitionFeeColumnName = "Tuition fee";
  const netSalaryColumnName = "Net Salary";
  const marginColumnName = "Margin";

  const columns = [
    { name: "sno", align: "center" },
    { name: "name", align: "left" },
    ...(isCategoryStandardTab ? [{ name: "students", label: "Students", align: "center" }] : []),
    {
      name: tuitionFeeColumnName,
      label: `${tuitionFeeColumnName} (${formatCurrency(totals.tuitionFee)})`,
      align: "center",
    },
    {
      name: netSalaryColumnName,
      label: `${netSalaryColumnName} (${formatCurrency(totals.netSalary)})`,
      align: "center",
    },
    {
      name: marginColumnName,
      label: `${marginColumnName} (${formatCurrency(totals.margin)})`,
      align: "center",
    },
  ];

  const hasRows = tableRows.length > 0;

  useEffect(() => {
    const paginatedData = isCategoryStandardTab
      ? data.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
      : data;

    if (isCategoryStandardTab) {
      setTableRows(
        paginatedData.map((item, index) => {
          const margin = getComputedMargin(item, item.netSalary);
          const marginPercentage =
            Number(item?.tuitionFee) > 0 ? Math.round((margin / Number(item.tuitionFee)) * 100) : 0;

          return {
            sno: (
              <ArgonTypography variant="caption" fontWeight="medium" color="text">
                {(currentPage - 1) * rowsPerPage + index + 1}
              </ArgonTypography>
            ),
            name: (
              <ArgonTypography textTransform="none" variant="button" fontWeight="medium">
                {item.className || "Unassigned"}
              </ArgonTypography>
            ),
            [tuitionFeeColumnName]: (
              <ArgonTypography variant="caption" color="success" fontWeight="medium">
                ₹{item.tuitionFee}
              </ArgonTypography>
            ),
            [netSalaryColumnName]: (
              <ArgonTypography variant="caption" color="text" fontWeight="medium">
                ₹{item.netSalary}
              </ArgonTypography>
            ),
            [marginColumnName]: (
              <ArgonTypography variant="caption" color="info" fontWeight="medium">
                ₹{margin} ({marginPercentage}%)
              </ArgonTypography>
            ),
            students: (
              <ArgonBox display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                <ArgonTypography variant="caption" color="text" fontWeight="medium">
                  {item.students?.length || 0}
                </ArgonTypography>
                <IconButton
                  size="small"
                  aria-label="View students"
                  onClick={() => openStudentsModal(item.className, item.students)}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </ArgonBox>
            ),
          };
        })
      );
    } else {
      setTableRows(
        paginatedData.map((item, index) => {
          const netSalary = getDisplayNetSalary(item);
          const margin = getComputedMargin(item, netSalary);
          const marginPercentage =
            Number(item?.tuitionFee) > 0 ? Math.round((margin / Number(item.tuitionFee)) * 100) : 0;

          return {
            sno: (
              <ArgonTypography variant="caption" fontWeight="medium" color="text">
                {(currentPage - 1) * rowsPerPage + index + 1}
              </ArgonTypography>
            ),
            name: (
              <ArgonBox display="flex" alignItems="center" px={0.5} py={0.1}>
                <ArgonBox mr={2}>
                  <ArgonAvatar
                    src={item.tutorProfileImage || item.parentProfileImage}
                    alt={item.tutorName || item.parentName}
                    size="sm"
                    variant="rounded"
                  />
                </ArgonBox>
                <ArgonBox display="flex" flexDirection="column">
                  <ArgonTypography textTransform="none" variant="button" fontWeight="medium">
                    {item.tutorName || item.parentName}
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    {item.tutorEmail || item.parentEmail}
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>
            ),
            [tuitionFeeColumnName]: (
              <ArgonTypography variant="caption" color="success" fontWeight="medium">
                ₹{item.tuitionFee}
              </ArgonTypography>
            ),
            [netSalaryColumnName]: (
              <ArgonTypography variant="caption" color="text" fontWeight="medium">
                ₹{netSalary}
              </ArgonTypography>
            ),
            [marginColumnName]: (
              <ArgonTypography variant="caption" color="info" fontWeight="medium">
                ₹{margin} ({marginPercentage}%)
              </ArgonTypography>
            ),
          };
        })
      );
    }
  }, [data, isCategoryStandardTab, currentPage, rowsPerPage]);

  useEffect(() => {
    fetchIncomeBreakdown();
  }, [activeTab, fromDate, toDate, search, currentPage, rowsPerPage]);

  const fetchIncomeBreakdown = async () => {
    setLoading(true);
    try {
      const params = {
        groupBy: isCategoryStandardTab ? "tutor" : activeTab === "Clients" ? "parent" : "tutor",
        from: fromDate ? fromDate.format("YYYY-MM-DD") : undefined,
        to: toDate ? toDate.format("YYYY-MM-DD") : undefined,
        search: search || undefined,
        page: isCategoryStandardTab ? 1 : currentPage,
        limit: isCategoryStandardTab ? 1000 : rowsPerPage,
      };
      const response = await getIncomeBreakdown(params);

      if (isCategoryStandardTab) {
        const classWiseData = buildClassWiseBreakdown(response.data || []);
        setData(classWiseData);
        setTotalItems(classWiseData.length);
      } else {
        setData(response.data || []);
        setTotalItems(response?.pagination?.totalRecords || 0);
      }
    } catch (error) {
      console.error("Error fetching income breakdown:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetFilters = () => {
    setFromDate(null);
    setToDate(null);
    setSearch("");
    setCurrentPage(1);
  };

  const tabsData = [
    { label: "Clients", content: "" },
    { label: "Tutors", content: "" },
    { label: "Category_Standard", content: "" },
  ];

  // Prepare chart data
  const chartData = {
    labels: data.map((item) => item.tutorName || item.parentName || ""),
    datasets: [
      {
        label: "Net Salary",
        data: data.map((item) => getDisplayNetSalary(item)),
        backgroundColor: "rgba(94, 200, 227, 0.7)",
        borderColor: "rgba(94, 200, 227, 1)",
        borderWidth: 1,
      },
      {
        label: "Tuition Fee",
        data: data.map((item) => item.tuitionFee || 0),
        backgroundColor: "rgba(129, 195, 255, 0.7)",
        borderColor: "rgba(129, 195, 255, 1)",
        borderWidth: 1,
      },
      {
        label: "Margin",
        data: data.map((item) => getComputedMargin(item)),
        backgroundColor: "rgba(255, 228, 163, 0.7)",
        borderColor: "rgba(255, 228, 163, 1)",
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          font: {
            size: 12,
          },
          padding: 15,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            label += "₹" + context.parsed.y.toLocaleString("en-IN");
            return label;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return "₹" + value.toLocaleString("en-IN");
          },
        },
      },
      x: {
        ticks: {
          maxRotation: 0,
          minRotation: 0,
        },
      },
    },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={2}>
        <ArgonBox mb={2}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                Income Breakdown
              </ArgonTypography>

              <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <ArgonBox sx={{ height: "36px" }}>
                  <ApnaTabs
                    tabsData={tabsData}
                    onTabChange={(tab) => {
                      setActiveTab(tab);
                      setCurrentPage(1);
                    }}
                  />
                </ArgonBox>
                <ArgonBox sx={{ height: "36px" }} display="flex" gap={2} alignItems="center">
                  <CustomDatePicker
                    label="From"
                    value={fromDate}
                    onChange={(newValue) => {
                      setFromDate(newValue);
                      setCurrentPage(1);
                    }}
                    width={160}
                    maxDate={toDate || dayjs()} // future date disable or toDate
                  />
                  <CustomDatePicker
                    label="To"
                    value={toDate}
                    onChange={(newValue) => {
                      setToDate(newValue);
                      setCurrentPage(1);
                    }}
                    width={160}
                    minDate={fromDate}
                    maxDate={dayjs()} // future date disable
                  />
                  {/* <ArgonInput
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                    sx={{ width: 200 }}
                  /> */}
                  <ArgonButton
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ marginLeft: "10px" }}
                    onClick={fetchIncomeBreakdown}
                  >
                    Filter
                  </ArgonButton>
                  <ArgonButton
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={handleResetFilters}
                    disabled={!fromDate && !toDate && !search}
                  >
                    Reset
                  </ArgonButton>
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>

            {/* Chart Section */}
            {!loading && data.length > 0 && activeTab !== "Category_Standard" && (
              <ArgonBox p={3}>
                <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                  Income Breakdown Chart
                </ArgonTypography>
                <ArgonBox sx={{ height: "400px", position: "relative" }}>
                  <Bar data={chartData} options={chartOptions} />
                </ArgonBox>
              </ArgonBox>
            )}

            <ArgonBox
              sx={{
                "& th": {
                  fontSize: "12px", // Increased column header font size
                },
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    Fetching{" "}
                    {activeTab === "Clients"
                      ? "clients"
                      : activeTab === "Tutors"
                      ? "tutors"
                      : "data"}
                    ...
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Please wait while we load the latest data.
                  </ArgonTypography>
                </ArgonBox>
              ) : !hasRows ? (
                <ArgonBox display="flex" flexDirection="column" alignItems="center" p={5}>
                  <ArgonTypography variant="h6" color="text" mb={1}>
                    No income data found
                  </ArgonTypography>
                  <ArgonTypography variant="body2" color="text">
                    Try adjusting your filters or check back later.
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                <Table columns={columns} rows={tableRows} />
              )}
            </ArgonBox>
            <PaginationBar
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              totalItems={totalItems}
              onPageChange={(event, page) => setCurrentPage(page)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setCurrentPage(1);
              }}
            />
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />

      <Dialog
        open={isStudentsModalOpen}
        onClose={() => setIsStudentsModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{selectedClassName} - Students</DialogTitle>
        <DialogContent dividers>
          {selectedClassStudents.length === 0 ? (
            <ArgonTypography variant="button" color="secondary">
              No students found for this class.
            </ArgonTypography>
          ) : (
            <ArgonBox display="flex" flexDirection="column" gap={1}>
              {selectedClassStudents.map((studentName) => (
                <ArgonBox
                  key={studentName}
                  px={1.5}
                  py={1}
                  borderRadius="8px"
                  sx={{ backgroundColor: "#f5f7ff" }}
                >
                  <ArgonTypography variant="button" color="text" fontWeight="regular">
                    {studentName}
                  </ArgonTypography>
                </ArgonBox>
              ))}
            </ArgonBox>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export default Tables;
